var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment } from 'react';
import { useQuery } from '@apollo/client';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useToast, ConfirmDialog, Dialog, DialogLoading, Button, DialogError } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { EntryKind } from 'shared/domains/apollo/generated/types';
import { MoveEntryACLChanges } from './MoveEntryACLChanges';
import { MoveEntryConfirmationQueryDocument } from './MoveEntryConfirmation.gql';
import { useMoveEntry } from './useMoveEntry';
import { ROOT } from '../../utils/constants';
export function MoveEntryConfirmation(props) {
    const { sourceId, targetId, onClose } = props;
    const { addToast } = useToast();
    const [move, moveState] = useMoveEntry();
    const { data, error, loading, refetch } = useQuery(MoveEntryConfirmationQueryDocument, {
        variables: { sourceId, targetId, movingToRoot: targetId === ROOT },
    });
    const { source, target } = data || {};
    const sourceKind = (source === null || source === void 0 ? void 0 : source.kind) || EntryKind.Document;
    const targetName = (target === null || target === void 0 ? void 0 : target.name) || '';
    const handleConfirm = () => __awaiter(this, void 0, void 0, function* () {
        try {
            yield move(targetId, sourceId, sourceKind);
            if (sourceKind === EntryKind.Folder) {
                addToast({
                    appearance: 'success',
                    title: t({
                        message: `Folder moved to ${targetName}`,
                        comment: 'Move folder success toast',
                    }),
                });
            }
            else {
                addToast({
                    appearance: 'success',
                    title: t({
                        message: `Document moved to ${targetName}`,
                        comment: 'Move document success toast',
                    }),
                });
            }
            onClose();
        }
        catch (_a) {
            if (sourceKind === EntryKind.Folder) {
                addToast({
                    appearance: 'danger',
                    title: t({
                        message: 'Error occurred while moving folder',
                        comment: 'Move folder error toast',
                    }),
                });
            }
            else {
                addToast({
                    appearance: 'danger',
                    title: t({
                        message: 'Error occurred while moving document',
                        comment: 'Move document error toast',
                    }),
                });
            }
        }
    });
    if (sourceId === targetId) {
        // Can happen if you move a folder onto itself, no error is expected, just do nothing
        return null;
    }
    if (loading) {
        return (<Dialog>
        <DialogLoading />
      </Dialog>);
    }
    if (error) {
        return (<Dialog>
        <DialogError title={t({ message: 'Error', comment: 'Error loading move entry confirmation' })} description={t({
                message: 'Something went wrong, please try again later',
                comment: 'Error loading move entry confirmation',
            })} actions={<Fragment>
              <Button type="button" appearance="secondary" size="small" onClick={onClose}>
                <Trans comment="Error loading move entry confirmation - Close button label">Close</Trans>
              </Button>
              <Button type="button" appearance="primary" size="small" onClick={() => refetch()}>
                <Trans comment="Error loading move entry confirmation - Retry button label">Retry</Trans>
              </Button>
            </Fragment>}/>
      </Dialog>);
    }
    return (<ConfirmDialog icon={<Falcon icon="arrow-right"/>} title={t({
            message: 'Confirm move',
            comment: 'Title for quick move document dialog (from document view and files)',
        })} appearance="primary" size="sm" loading={moveState.loading} confirm={t({ message: 'Move', comment: 'Move document button label' })} cancel={t({ message: 'Cancel', comment: 'Cancel button label' })} onConfirm={handleConfirm} onCancel={onClose}>
      <MoveEntryACLChanges fromSource={(data === null || data === void 0 ? void 0 : data.source) || {}} fromTarget={(data === null || data === void 0 ? void 0 : data.target) || {}}/>
    </ConfirmDialog>);
}
